import { useState, useEffect } from "react";
import { http, API } from "@/lib/api";
import { toast } from "sonner";
import { Search, Download, Trash2, Mail, Eye, FileText, ChevronLeft, Copy, Check, Printer, X, Link2 } from "lucide-react";

export default function AdminGuestReleases() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [emailing, setEmailing] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { load(); backfill(); }, []);

  const backfill = async () => {
    try { await http.post("/guest-release/admin/backfill-tokens"); } catch (e) { /* silent */ }
  };

  const load = async (q = "") => {
    try {
      const { data } = await http.get(`/guest-release/admin/list?search=${q}`);
      setReleases(data);
    } catch { toast.error("Failed to load releases"); }
    finally { setLoading(false); }
  };

  const doSearch = () => { setLoading(true); load(search); };

  const deleteRelease = async (id) => {
    if (!window.confirm("Delete this release permanently?")) return;
    setDeleting(id);
    try {
      await http.delete(`/guest-release/admin/${id}`);
      toast.success("Release deleted");
      setReleases(r => r.filter(x => x.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch { toast.error("Delete failed"); }
    finally { setDeleting(null); }
  };

  const downloadPdf = (id, name, release) => {
    const pdfUrl = release?.pdf_url;
    if (pdfUrl) {
      window.open(`${API.replace('/api', '')}${pdfUrl}`, "_blank");
    } else {
      toast.error("PDF not available. Try refreshing the page.");
    }
  };

  const emailRelease = async (id) => {
    setEmailing(id);
    try {
      await http.post(`/guest-release/admin/${id}/email`);
      toast.success("Release emailed to guest");
    } catch { toast.error("Email failed"); }
    finally { setEmailing(null); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("token");
    window.open(`${API}/guest-release/admin-export/csv?token=${token}`, "_blank");
  };

  const copyLink = () => {
    const url = `${window.location.origin}/guest-release`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success("Shareable link copied!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const privacyLabel = (p) => {
    const m = { full_name: "Full Name", first_only: "First Name Only", anonymous: "Anonymous" };
    return m[p] || p;
  };

  // Detail view
  if (selected) return (
    <div data-testid="release-detail">
      <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-zinc-400 hover:text-[#F4F1EA] mb-6 text-sm" data-testid="back-to-list">
        <ChevronLeft size={16} /> Back to All Releases
      </button>

      <div className="border border-white/10 rounded-xl bg-[#0a0a0a] p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-[#F4F1EA] font-display text-xl">{selected.first_name} {selected.last_name}</h3>
          <div className="flex gap-2">
            <button onClick={() => downloadPdf(selected.id, `${selected.first_name}_${selected.last_name}`, selected)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-zinc-400 hover:text-[#F4F1EA] hover:border-white/20 transition-colors"
              data-testid="detail-download-pdf">
              <Download size={13} /> PDF
            </button>
            <button onClick={() => window.print()}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-zinc-400 hover:text-[#F4F1EA] hover:border-white/20 transition-colors"
              data-testid="detail-print">
              <Printer size={13} /> Print
            </button>
            <button onClick={() => emailRelease(selected.id)} disabled={emailing === selected.id}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-zinc-400 hover:text-[#F4F1EA] hover:border-white/20 transition-colors disabled:opacity-40"
              data-testid="detail-email">
              <Mail size={13} /> {emailing === selected.id ? "Sending..." : "Email Guest"}
            </button>
            <button onClick={() => deleteRelease(selected.id)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-colors"
              data-testid="detail-delete">
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </div>

        {/* Guest Info */}
        <DetailSection title="Guest Information">
          <Grid>
            <DField label="Email" value={selected.email} />
            <DField label="Phone" value={selected.phone} />
            <DField label="City" value={selected.city} />
            <DField label="State" value={selected.state} />
            <DField label="Date" value={selected.date} />
          </Grid>
        </DetailSection>

        {/* Agreements */}
        <DetailSection title="Agreements">
          <Grid>
            <DField label="Recording Types" value={(selected.recording_types || []).join(", ")} />
            <DField label="Story Permission" value={selected.story_agree ? "Agreed" : "—"} />
            <DField label="Editing" value={selected.editing_agree ? "Agreed" : "—"} />
            <DField label="Compensation" value={selected.compensation_agree ? "Agreed" : "—"} />
            <DField label="Responsibility" value={selected.responsibility_agree ? "Agreed" : "—"} />
            <DField label="Liability Release" value={selected.liability_agree ? "Agreed" : "—"} />
          </Grid>
        </DetailSection>

        {/* Privacy */}
        <DetailSection title="Privacy & Options">
          <Grid>
            <DField label="Privacy Preference" value={privacyLabel(selected.privacy_preference)} />
            {selected.anonymous_options?.length > 0 && (
              <DField label="Anonymous Options" value={selected.anonymous_options.join(", ")} />
            )}
            <DField label="Receive Copy" value={selected.receive_copy} />
            <DField label="Notify Live" value={selected.notify_live} />
            <DField label="Future Projects" value={selected.future_projects} />
          </Grid>
        </DetailSection>

        {/* Emergency Contact */}
        {selected.emergency_name && (
          <DetailSection title="Emergency Contact">
            <Grid>
              <DField label="Name" value={selected.emergency_name} />
              <DField label="Phone" value={selected.emergency_phone} />
              <DField label="Relationship" value={selected.emergency_relationship} />
            </Grid>
          </DetailSection>
        )}

        {/* Audit Trail */}
        <DetailSection title="Audit Trail">
          <Grid>
            <DField label="IP Address" value={selected.ip_address} />
            <DField label="Submitted At" value={new Date(selected.submitted_at).toLocaleString()} />
            <DField label="Browser" value={selected.user_agent?.substring(0, 80) + "..."} />
          </Grid>
        </DetailSection>

        {/* PDF Link */}
        {selected.pdf_url && (
          <DetailSection title="PDF Document">
            <div className="flex items-center gap-3 flex-wrap">
              <a href={`${API.replace('/api', '')}${selected.pdf_url}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 text-[#C9A055] hover:underline text-sm" data-testid="pdf-link">
                <Link2 size={14} /> View PDF
              </a>
              <button onClick={() => {
                const url = `${window.location.origin}${selected.pdf_url}`;
                navigator.clipboard.writeText(url).then(() => toast.success("PDF link copied!"));
              }} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-zinc-400 hover:text-[#F4F1EA] transition-colors"
                data-testid="copy-pdf-link">
                <Copy size={12} /> Copy PDF Link
              </button>
            </div>
          </DetailSection>
        )}

        {/* Signature */}
        <DetailSection title="Digital Signature">
          <DField label="Typed Name" value={selected.typed_name} />
          {selected.signature_data && (
            <div className="mt-3 border border-white/10 rounded-lg bg-white p-4 inline-block">
              <img src={selected.signature_data} alt="Signature" className="h-20" data-testid="detail-signature-img" />
            </div>
          )}
        </DetailSection>
      </div>
    </div>
  );

  // List view
  return (
    <div data-testid="admin-guest-releases">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="text-[#F4F1EA] font-display text-xl">Guest Releases</h2>
          <p className="text-zinc-500 text-xs font-ui">{releases.length} submission{releases.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={copyLink} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-zinc-400 hover:text-[#F4F1EA] hover:border-white/20 transition-colors"
            data-testid="copy-share-link">
            {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
            {copied ? "Copied!" : "Copy Form Link"}
          </button>
          <button onClick={exportCsv} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-zinc-400 hover:text-[#F4F1EA] hover:border-white/20 transition-colors"
            data-testid="export-csv">
            <FileText size={13} /> Export CSV
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-2 mb-6">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && doSearch()}
            placeholder="Search by name or email..."
            className="w-full bg-transparent border border-white/10 text-[#F4F1EA] pl-9 pr-4 py-2.5 rounded-lg text-sm focus:border-[#C9A055] focus:outline-none transition-colors font-ui"
            data-testid="search-releases" />
        </div>
        <button onClick={doSearch} className="px-4 py-2.5 bg-[#C9A055] text-black rounded-lg text-xs font-ui tracking-wider uppercase hover:bg-[#d4af37] transition-colors"
          data-testid="search-btn">Search</button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-500 text-sm animate-pulse">Loading...</div>
      ) : releases.length === 0 ? (
        <div className="text-center py-16 border border-white/10 rounded-xl">
          <FileText size={32} className="mx-auto text-zinc-600 mb-3" />
          <p className="text-zinc-500 text-sm">No guest releases found</p>
          <p className="text-zinc-600 text-xs mt-1">Share the form link to start collecting releases</p>
        </div>
      ) : (
        <div className="space-y-2">
          {releases.map(r => (
            <div key={r.id} className="flex items-center justify-between p-4 border border-white/10 rounded-xl bg-[#0a0a0a] hover:border-white/[0.15] transition-colors"
              data-testid={`release-row-${r.id}`}>
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-9 h-9 rounded-full bg-white/[0.06] flex items-center justify-center text-[#C9A055] text-xs font-bold flex-shrink-0">
                  {r.first_name?.[0]}{r.last_name?.[0]}
                </div>
                <div className="min-w-0">
                  <p className="text-[#F4F1EA] text-sm font-medium truncate">{r.first_name} {r.last_name}</p>
                  <p className="text-zinc-500 text-xs truncate">{r.email} · {r.city}, {r.state}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-zinc-600 text-[10px] tracking-wider hidden sm:block">
                  {new Date(r.submitted_at).toLocaleDateString()}
                </span>
                <span className={`text-[9px] tracking-wider uppercase px-2 py-0.5 rounded-full ${
                  r.privacy_preference === "anonymous" ? "bg-amber-500/10 text-amber-500" :
                  r.privacy_preference === "first_only" ? "bg-blue-500/10 text-blue-400" :
                  "bg-emerald-500/10 text-emerald-500"
                }`}>{privacyLabel(r.privacy_preference)}</span>
                <button onClick={() => setSelected(r)} className="p-1.5 text-zinc-500 hover:text-[#F4F1EA] transition-colors" data-testid={`view-release-${r.id}`}>
                  <Eye size={15} />
                </button>
                <button onClick={() => downloadPdf(r.id, `${r.first_name}_${r.last_name}`, r)}
                  className="p-1.5 text-zinc-500 hover:text-[#F4F1EA] transition-colors" data-testid={`download-release-${r.id}`}>
                  <Download size={15} />
                </button>
                <button onClick={() => deleteRelease(r.id)} disabled={deleting === r.id}
                  className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors disabled:opacity-40" data-testid={`delete-release-${r.id}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="border-t border-white/[0.06] pt-4">
      <h4 className="text-[10px] tracking-[0.2em] uppercase text-[#C9A055]/70 mb-3 font-ui">{title}</h4>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>;
}

function DField({ label, value }) {
  return (
    <div>
      <span className="text-zinc-500 text-[10px] uppercase tracking-wider">{label}</span>
      <p className="text-[#F4F1EA] text-sm mt-0.5 break-words">{value || "—"}</p>
    </div>
  );
}
